import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { useLanguage } from "../context/LanguageContext";
import { 
  Play, Clock, Trophy, Target, Star, Lock, CheckCircle2,
  Zap, Brain, Rocket, Crown, Sparkles
} from "lucide-react";

const Simulations = ({ auth }) => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [generatingTest, setGeneratingTest] = useState(null);
  const [completedTests, setCompletedTests] = useState({});
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [loading, setLoading] = useState(true);

  const isFreeUser = !auth.user || auth.user.subscription_tier === "free";
  const FREE_SIMULATION_ID = "sim_easy_1"; // Only this one is free

  useEffect(() => {
    fetchUserProgress();
  }, []);

  const fetchUserProgress = async () => {
    try {
      const headers = auth.token ? { Authorization: `Bearer ${auth.token}` } : {};
      const response = await axios.get(`${API}/progress/my-tests`, {
        withCredentials: true,
        headers
      });
      
      // Build a map of test_id -> best result
      const progressMap = {};
      (response.data.results || []).forEach(result => {
        const testId = result.simulation_id || result.test_id;
        if (testId && testId.startsWith('sim_')) {
          // Keep the best score for each simulation
          if (!progressMap[testId] || result.score > progressMap[testId].score) {
            progressMap[testId] = result;
          }
        }
      });
      setCompletedTests(progressMap);
    } catch (error) {
      console.error("Error fetching progress:", error);
    } finally {
      setLoading(false);
    }
  };

  const generateSimulations = () => {
    const simulations = [];
    
    for (let i = 1; i <= 10; i++) {
      simulations.push({
        id: `sim_easy_${i}`,
        title: `Easy Simulation ${i}`,
        difficulty: "easy",
        description: `Build your foundation with basic questions`,
        questions: 20,
        timeLimit: 45,
        passScore: 75,
        icon: <Star className="w-5 h-5" />,
        color: "bg-green-500",
        bgColor: "bg-green-50",
        borderColor: "border-green-200"
      });
    }
    
    for (let i = 1; i <= 10; i++) {
      simulations.push({
        id: `sim_medium_${i}`,
        title: `Medium Simulation ${i}`,
        difficulty: "medium",
        description: `Challenge yourself with intermediate questions`,
        questions: 20,
        timeLimit: 45,
        passScore: 75,
        icon: <Brain className="w-5 h-5" />,
        color: "bg-yellow-500",
        bgColor: "bg-yellow-50",
        borderColor: "border-yellow-200"
      });
    }
    
    for (let i = 1; i <= 10; i++) {
      simulations.push({
        id: `sim_hard_${i}`,
        title: `Hard Simulation ${i}`,
        difficulty: "hard",
        description: `Test yourself with challenging questions`,
        questions: 20,
        timeLimit: 45,
        passScore: 75,
        icon: <Rocket className="w-5 h-5" />,
        color: "bg-red-500",
        bgColor: "bg-red-50",
        borderColor: "border-red-200"
      });
    }
    
    return simulations;
  };

  const simulations = generateSimulations();
  const easyTests = simulations.filter(s => s.difficulty === "easy");
  const mediumTests = simulations.filter(s => s.difficulty === "medium");
  const hardTests = simulations.filter(s => s.difficulty === "hard");

  const startSimulation = async (simulation) => {
    // Check if free user trying to access locked content
    if (isFreeUser && simulation.id !== FREE_SIMULATION_ID) {
      setShowUpgradeModal(true);
      return;
    }

    setGeneratingTest(simulation.id);
    try {
      const response = await axios.post(`${API}/simulations/generate`, {
        simulation_id: simulation.id,
        difficulty: simulation.difficulty,
        question_count: simulation.questions,
        time_limit: simulation.timeLimit
      }, {
        withCredentials: true,
        headers: auth.token ? { Authorization: `Bearer ${auth.token}` } : {}
      });
      
      navigate(`/test/${response.data.test_id}`, { 
        state: { 
          questions: response.data.questions, 
          timeLimit: response.data.time_limit,
          title: simulation.title,
          description: simulation.description,
          simulationId: simulation.id,
          isSimulation: true
        } 
      });
    } catch (error) {
      toast.error("Failed to generate simulation. Please try again.");
    } finally {
      setGeneratingTest(null);
    }
  };

  const getCompletionStats = (tests) => {
    const completed = tests.filter(t => completedTests[t.id]).length;
    return { completed, total: tests.length, percentage: (completed / tests.length) * 100 };
  };

  const easyStats = getCompletionStats(easyTests);
  const mediumStats = getCompletionStats(mediumTests);
  const hardStats = getCompletionStats(hardTests);

  const renderSimulationCard = (simulation, index) => {
    const result = completedTests[simulation.id];
    const isCompleted = !!result;
    const score = result?.score || 0;
    const isPassed = isCompleted && score >= simulation.passScore;
    const isLocked = isFreeUser && simulation.id !== FREE_SIMULATION_ID;
    const isFreeOne = simulation.id === FREE_SIMULATION_ID;
    
    return (
      <Card 
        key={simulation.id}
        className={`border ${simulation.borderColor} transition-all relative ${
          isLocked ? 'opacity-75 hover:opacity-90' : 'hover:shadow-md'
        } ${isCompleted ? 'opacity-80' : ''}`}
        data-testid={`simulation-${simulation.id}`}
      >
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 ${isLocked ? 'bg-slate-400' : simulation.color} rounded-lg flex items-center justify-center text-white`}>
                {isLocked ? <Lock className="w-4 h-4" /> : simulation.icon}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-medium text-slate-900">{simulation.title}</h3>
                  {isFreeOne && isFreeUser && (
                    <Badge className="bg-green-100 text-green-700 text-xs">FREE</Badge>
                  )}
                </div>
                <p className="text-xs text-slate-500">{simulation.questions} questions • {simulation.timeLimit} min</p>
              </div>
            </div>
            {isCompleted && (
              <Badge className={isPassed ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}>
                {score}%
              </Badge>
            )}
            {isLocked && (
              <Badge className="bg-yellow-100 text-yellow-700">
                <Lock className="w-3 h-3 mr-1" />
                Pro
              </Badge>
            )}
          </div>
          
          <div className="flex items-center justify-between mt-3">
            {isCompleted ? (
              <div className="flex items-center gap-1 text-green-600 text-sm">
                <CheckCircle2 className="w-4 h-4" />
                <span>Completed</span>
              </div>
            ) : isLocked ? (
              <span className="text-sm text-slate-500">Upgrade to unlock</span>
            ) : (
              <span className="text-sm text-slate-500">Not started</span>
            )}
            <Button 
              size="sm"
              onClick={() => startSimulation(simulation)}
              disabled={generatingTest === simulation.id}
              className={
                isLocked 
                  ? "bg-yellow-500 hover:bg-yellow-600" 
                  : isCompleted 
                    ? "bg-slate-600 hover:bg-slate-700" 
                    : `${simulation.color} hover:opacity-90`
              }
            >
              {generatingTest === simulation.id ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                </span>
              ) : isLocked ? (
                <>
                  <Crown className="w-4 h-4 mr-1" />
                  Unlock
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-1" />
                  {isCompleted ? "Retry" : "Start"}
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Upgrade Modal
  const UpgradeModal = () => (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowUpgradeModal(false)}>
      <Card className="max-w-md w-full border-2 border-yellow-400 shadow-2xl" onClick={e => e.stopPropagation()}>
        <CardContent className="p-6 text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Lock className="w-8 h-8 text-white" />
          </div>
          
          <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">
            Unlock All 30 Simulations
          </h3>
          <p className="text-slate-600 mb-4">
            You've tried the free Easy Simulation! Upgrade to access all 30 progressive tests: 
            10 Easy, 10 Medium, and 10 Hard simulations.
          </p>

          <div className="bg-slate-50 rounded-lg p-4 mb-4 text-left">
            <p className="text-sm font-medium text-slate-700 mb-2">With Pro/Premium you also get:</p>
            <ul className="space-y-1.5 text-sm text-slate-600">
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                500+ chapter-wise practice questions
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                3 realistic mock tests (45 min each)
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                200+ interactive flashcards
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                Weak area detection & analytics
              </li>
            </ul>
          </div>

          <div className="space-y-3">
            <Link to="/pricing" className="block">
              <Button className="w-full bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-500 hover:to-amber-600 text-white font-semibold h-12">
                <Crown className="w-5 h-5 mr-2" />
                View Plans — From CAD $9.99
              </Button>
            </Link>
            <p className="text-xs text-slate-500">
              <span className="line-through">$29.99</span> → <span className="font-semibold text-red-600">$14.99 Lifetime</span> (50% OFF)
            </p>
            <Button variant="ghost" onClick={() => setShowUpgradeModal(false)} className="w-full">
              Maybe Later
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <DashboardLayout auth={auth}>
      <div className="space-y-8" data-testid="simulations-page">
        {/* Header */}
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">{t('nav.simulations')}</h1>
          <p className="text-slate-600">
            {language === 'fr' 
              ? 'Tests de pratique progressifs pour renforcer votre confiance'
              : 'Progressive practice tests to build your exam confidence'
            }
          </p>
        </div>

        {/* Free User Banner */}
        {isFreeUser && (
          <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border-2 border-yellow-300 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-yellow-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-slate-900">Free Trial: 1 Easy Simulation</p>
                <p className="text-sm text-slate-600 mt-0.5">
                  Try our free Easy Simulation to experience the test format. 
                  Upgrade to unlock all 30 simulations, mock tests, chapter practice & more!
                </p>
              </div>
              <Link to="/pricing">
                <Button className="bg-yellow-500 hover:bg-yellow-600 text-white">
                  <Crown className="w-4 h-4 mr-2" />
                  Upgrade
                </Button>
              </Link>
            </div>
          </div>
        )}

        {/* Progress Overview */}
        <Card className="border-0 shadow-sm bg-gradient-to-r from-green-50 via-yellow-50 to-red-50">
          <CardContent className="p-6">
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Star className="w-5 h-5 text-green-600" />
                  <span className="font-medium text-green-700">Easy</span>
                  {isFreeUser && <Badge className="text-xs bg-green-100 text-green-700">1 Free</Badge>}
                </div>
                <Progress value={isFreeUser ? 0 : easyStats.percentage} className="h-2 mb-1" />
                <p className="text-sm text-slate-600">
                  {isFreeUser ? "1 free / 10 total" : `${easyStats.completed}/10 completed`}
                </p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Brain className="w-5 h-5 text-yellow-600" />
                  <span className="font-medium text-yellow-700">Medium</span>
                  {isFreeUser && <Lock className="w-4 h-4 text-slate-400" />}
                </div>
                <Progress value={isFreeUser ? 0 : mediumStats.percentage} className="h-2 mb-1" />
                <p className="text-sm text-slate-600">
                  {isFreeUser ? "Upgrade to unlock" : `${mediumStats.completed}/10 completed`}
                </p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Rocket className="w-5 h-5 text-red-600" />
                  <span className="font-medium text-red-700">Hard</span>
                  {isFreeUser && <Lock className="w-4 h-4 text-slate-400" />}
                </div>
                <Progress value={isFreeUser ? 0 : hardStats.percentage} className="h-2 mb-1" />
                <p className="text-sm text-slate-600">
                  {isFreeUser ? "Upgrade to unlock" : `${hardStats.completed}/10 completed`}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Info Banner */}
        <div className="bg-truenorth-50 border border-truenorth-100 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Target className="w-5 h-5 text-truenorth mt-0.5" />
            <div>
              <p className="font-medium text-truenorth">How Simulations Work</p>
              <p className="text-sm text-slate-600 mt-1">
                Each simulation has 20 questions and 45 minutes - just like the real test. 
                Start with Easy, progress to Medium, then challenge yourself with Hard. 
                Score 75% or higher to pass!
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="easy" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="easy" className="flex items-center gap-2">
              <Star className="w-4 h-4" />
              Easy {isFreeUser ? "(1 Free)" : `(${easyStats.completed}/10)`}
            </TabsTrigger>
            <TabsTrigger value="medium" className="flex items-center gap-2">
              <Brain className="w-4 h-4" />
              Medium {isFreeUser && <Lock className="w-3 h-3" />}
            </TabsTrigger>
            <TabsTrigger value="hard" className="flex items-center gap-2">
              <Rocket className="w-4 h-4" />
              Hard {isFreeUser && <Lock className="w-3 h-3" />}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="easy">
            <div className="grid md:grid-cols-2 gap-4">
              {easyTests.map((sim, idx) => renderSimulationCard(sim, idx))}
            </div>
          </TabsContent>

          <TabsContent value="medium">
            {isFreeUser && (
              <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                <Lock className="w-6 h-6 text-yellow-600 mx-auto mb-2" />
                <p className="font-medium text-slate-900">Medium Simulations Locked</p>
                <p className="text-sm text-slate-600 mb-3">Upgrade to Pro or Premium to access all 10 Medium simulations</p>
                <Link to="/pricing">
                  <Button className="bg-yellow-500 hover:bg-yellow-600 text-white">
                    <Crown className="w-4 h-4 mr-2" /> Unlock Now
                  </Button>
                </Link>
              </div>
            )}
            <div className="grid md:grid-cols-2 gap-4">
              {mediumTests.map((sim, idx) => renderSimulationCard(sim, idx))}
            </div>
          </TabsContent>

          <TabsContent value="hard">
            {isFreeUser && (
              <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                <Lock className="w-6 h-6 text-yellow-600 mx-auto mb-2" />
                <p className="font-medium text-slate-900">Hard Simulations Locked</p>
                <p className="text-sm text-slate-600 mb-3">Upgrade to Pro or Premium to access all 10 Hard simulations</p>
                <Link to="/pricing">
                  <Button className="bg-yellow-500 hover:bg-yellow-600 text-white">
                    <Crown className="w-4 h-4 mr-2" /> Unlock Now
                  </Button>
                </Link>
              </div>
            )}
            <div className="grid md:grid-cols-2 gap-4">
              {hardTests.map((sim, idx) => renderSimulationCard(sim, idx))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Upgrade Modal */}
      {showUpgradeModal && <UpgradeModal />}
    </DashboardLayout>
  );
};

export default Simulations;
